/**
 * Parse an Infinity Functional Neurology "Appointments For" PDF into the four
 * columns the Boards page cares about: Time, Patient, Provider, Appointment
 * Type. Runs entirely in the browser via pdfjs-dist — the PDF bytes never
 * leave the user's machine until the resulting CSV is uploaded to Drive.
 *
 * Returned `weekFolderName` follows the convention used by the existing
 * weekly-permissions workflow: "MM.DD.YY - MM.DD.YY" where the dates are the
 * Monday and Friday of the week the PDF's header line points at, regardless
 * of which weekday the PDF itself covers.
 */

import * as pdfjsLib from "pdfjs-dist";
// Vite-friendly worker import — bundles the worker script alongside the app
// and gives back a URL we hand to pdfjs's GlobalWorkerOptions.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — `?url` is a Vite asset query, not a real module path.
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker as string;

export interface AppointmentRow {
  time: string;
  patient: string;
  provider: string;
  appointmentType: string;
}

export interface ParsedAppointmentsPdf {
  /** Display label from the header line, e.g. "Monday, June 22, 2026". */
  sourceDateLabel: string;
  /** "MM.DD.YY - MM.DD.YY" — Monday through Friday of the source date's week. */
  weekFolderName: string;
  /** Per-row parsed values, in document order. */
  rows: AppointmentRow[];
  /** Ready-to-upload CSV string (four-column, RFC-4180 quoted). */
  csvText: string;
}

const HEADER_RE =
  /Appointments\s*For\s*:?\s*([A-Za-z]+),\s*([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})/i;

const MONTHS: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

const TARGET_HEADERS = ["Time", "Patient", "Provider", "Appointment Type"] as const;

interface TextItem {
  str: string;
  x: number;
  y: number;
  /** width of the item in PDF user-space units — handy for header detection */
  width: number;
}

/** Read every page and return text items in a flat list with positional metadata. */
async function extractTextItems(file: File): Promise<TextItem[]> {
  const buf = await file.arrayBuffer();
  const doc = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
  const out: TextItem[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const content = await page.getTextContent();
    // Page-local y is bottom-origin in PDF space; flip to a top-origin sort
    // key by negating so later groupBy works left-to-right, top-to-bottom.
    // Page offset keeps multi-page docs in order without overlap.
    const pageYOffset = (p - 1) * 10000;
    for (const item of content.items as Array<{
      str?: string;
      transform?: number[];
      width?: number;
    }>) {
      if (!item.str || !item.transform) continue;
      const x = item.transform[4] ?? 0;
      const yRaw = item.transform[5] ?? 0;
      out.push({
        str: item.str,
        x,
        y: pageYOffset + (10000 - yRaw),
        width: item.width ?? 0,
      });
    }
    page.cleanup();
  }
  await doc.cleanup();
  await doc.destroy();
  return out;
}

/**
 * Group items by row using a y-coordinate tolerance. PDF text from a printed
 * row often arrives in fragments at slightly different y values; 4 user-space
 * units catches them without merging adjacent rows in a tight schedule.
 */
function groupRows(items: TextItem[]): TextItem[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  const rows: TextItem[][] = [];
  const TOL = 4;
  let current: TextItem[] = [sorted[0]];
  let currentY = sorted[0].y;
  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].y - currentY) <= TOL) {
      current.push(sorted[i]);
    } else {
      rows.push(current.sort((a, b) => a.x - b.x));
      current = [sorted[i]];
      currentY = sorted[i].y;
    }
  }
  rows.push(current.sort((a, b) => a.x - b.x));
  return rows;
}

/** Concatenate adjacent items in a row that share the same logical cell. */
function rowToText(row: TextItem[]): string {
  return row.map((r) => r.str).join("").replace(/\s+/g, " ").trim();
}

/**
 * Tokenize a row into column-labeled tokens. pdfjs already delivers
 * multi-word labels like "Appointment Type" / "Bill Type" / "Phone Number"
 * as single items, so we just drop the whitespace-only items between them.
 */
function rowToTokens(row: TextItem[]): { x: number; text: string }[] {
  return row
    .filter((it) => it.str.trim().length > 0)
    .map((it) => ({ x: it.x, text: it.str.trim() }));
}

/**
 * Find the header row and return x-anchors for ALL columns present in it —
 * not just the four we care about. Capturing Duration / Bill Type / Phone
 * Number too lets the nearest-anchor bucketer route their data away from
 * Appointment Type (where it would otherwise bleed in).
 */
function findHeaderAnchors(rows: TextItem[][]):
  | {
      rowIndex: number;
      /** Anchors we emit. */
      target: Record<typeof TARGET_HEADERS[number], number>;
      /** All header tokens (label → x). Includes Duration, Bill Type, etc. */
      all: { label: string; x: number }[];
    }
  | null {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const joined = rowToText(row).toLowerCase();
    if (
      joined.includes("time") &&
      joined.includes("patient") &&
      joined.includes("provider") &&
      joined.includes("appointment")
    ) {
      const tokens = rowToTokens(row);
      const normalized = tokens.map((c) => ({
        x: c.x,
        label: c.text.trim(),
      }));
      const findX = (label: string): number | undefined => {
        const lc = label.toLowerCase();
        const hit = normalized.find((c) => c.label.toLowerCase() === lc);
        if (hit) return hit.x;
        const loose = normalized.find((c) => c.label.toLowerCase().startsWith(lc));
        return loose?.x;
      };
      const t = findX("Time");
      const p = findX("Patient");
      const pr = findX("Provider");
      const at =
        findX("Appointment Type") ?? findX("Appointment") ?? findX("Type");
      if (t != null && p != null && pr != null && at != null) {
        return {
          rowIndex: i,
          target: {
            Time: t,
            Patient: p,
            Provider: pr,
            "Appointment Type": at,
          },
          all: normalized,
        };
      }
    }
  }
  return null;
}

/**
 * Assign each item in a data row to the nearest column anchor by x position.
 * `allAnchors` includes ALL columns from the header (Duration / Bill Type /
 * Phone Number too); items closer to those anchors than to one of the four
 * we emit are dropped, preventing right-side bleed into Appointment Type.
 */
function bucketByColumn(
  row: TextItem[],
  target: Record<typeof TARGET_HEADERS[number], number>,
  allAnchors: { label: string; x: number }[],
): AppointmentRow {
  const cols: Record<typeof TARGET_HEADERS[number], string[]> = {
    Time: [],
    Patient: [],
    Provider: [],
    "Appointment Type": [],
  };
  const targetSet = new Set<string>([
    "Time",
    "Patient",
    "Provider",
    "Appointment Type",
    "Appointment",
    "Type",
  ]);
  // De-duplicate: when the header tokenizer produced both "Appointment" and
  // "Type", they share an x with "Appointment Type". Keep one anchor per
  // unique x to avoid double-counting in the nearest-anchor scan.
  const seenX = new Set<number>();
  const anchorList: { label: string; x: number; isTarget: boolean }[] = [];
  for (const a of allAnchors) {
    if (seenX.has(a.x)) continue;
    seenX.add(a.x);
    anchorList.push({
      label: a.label,
      x: a.x,
      isTarget: targetSet.has(a.label),
    });
  }
  anchorList.sort((a, b) => a.x - b.x);

  for (const item of row) {
    const trimmed = item.str.trim();
    if (!trimmed) continue;
    // Pick the column whose anchor is the LARGEST x ≤ item.x. Models a real
    // table layout where each cell's text starts at the column's left edge,
    // so an item between two anchors belongs to the LEFT column — not the
    // nearest one. (Mid-point nearest-anchor would misroute "Dr." into
    // Appointment Type when it sits just past the Provider/AT midpoint.)
    const TOL = 4; // catches minor anchor drift on the left edge
    let best: typeof anchorList[number] | null = null;
    for (const a of anchorList) {
      if (item.x + TOL >= a.x) {
        if (!best || a.x > best.x) best = a;
      }
    }
    // Fallback for an item that sits before all anchors — assign to first.
    if (!best) best = anchorList[0];
    if (!best.isTarget) continue; // Skip Duration / Bill Type / Phone Number.
    // Map the matched anchor label to its target column key. "Appointment"
    // and "Type" both fall under "Appointment Type" since they sit at the
    // same x band in the joined column.
    const key: typeof TARGET_HEADERS[number] =
      best.label === "Time"
        ? "Time"
        : best.label === "Patient"
          ? "Patient"
          : best.label === "Provider"
            ? "Provider"
            : "Appointment Type";
    cols[key].push(item.str);
  }

  // Join with spaces so multi-item cells like "Phone" + "Consultation" become
  // "Phone Consultation". The whitespace items between them were already
  // filtered out above; \s+/g collapses any doubled spaces.
  const join = (s: string[]) => s.join(" ").replace(/\s+/g, " ").trim();
  return {
    time: join(cols.Time),
    patient: join(cols.Patient),
    provider: join(cols.Provider),
    appointmentType: join(cols["Appointment Type"]),
  };
}

/** RFC-4180-ish CSV: wrap in quotes if the cell contains a quote, comma, or newline. */
function csvCell(s: string): string {
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowsToCsv(rows: AppointmentRow[]): string {
  const header = ["Time", "Patient", "Provider", "Appointment Type"].join(",");
  const body = rows
    .map((r) =>
      [r.time, r.patient, r.provider, r.appointmentType].map(csvCell).join(","),
    )
    .join("\n");
  return body ? `${header}\n${body}\n` : `${header}\n`;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Given a Date object, return the Monday-of-week and Friday-of-week
 * formatted as "MM.DD.YY" and joined as "MM.DD.YY - MM.DD.YY".
 */
export function weekFolderNameFromDate(d: Date): string {
  // JavaScript's Date.getDay(): Sun=0, Mon=1, …, Sat=6.
  const dayOfWeek = d.getDay();
  // Map to "days since Monday" — treating Sunday as the previous week's tail
  // so a Sunday-dated PDF (shouldn't happen for an appointments PDF but
  // worth handling) still falls in the Mon-Fri block of the prior week.
  const daysSinceMonday = (dayOfWeek + 6) % 7;
  const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - daysSinceMonday);
  const friday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 4);
  const fmt = (x: Date) =>
    `${pad2(x.getMonth() + 1)}.${pad2(x.getDate())}.${String(x.getFullYear()).slice(2)}`;
  return `${fmt(monday)} - ${fmt(friday)}`;
}

export async function parseAppointmentsPdf(file: File): Promise<ParsedAppointmentsPdf> {
  const items = await extractTextItems(file);
  if (items.length === 0) {
    throw new Error("PDF appears to be empty or unreadable.");
  }

  // 1. Header line → source date + week folder name.
  const fullText = items.map((i) => i.str).join(" ");
  const headerMatch = HEADER_RE.exec(fullText);
  if (!headerMatch) {
    throw new Error(
      'Could not find an "Appointments For:" line in this PDF. Is it the right document?',
    );
  }
  const [, dayName, monthName, dayStr, yearStr] = headerMatch;
  const monthIdx = MONTHS[monthName.toLowerCase()];
  if (monthIdx == null) {
    throw new Error(`Unrecognized month "${monthName}" in PDF header.`);
  }
  const date = new Date(parseInt(yearStr, 10), monthIdx, parseInt(dayStr, 10));
  const sourceDateLabel = `${dayName}, ${monthName} ${parseInt(dayStr, 10)}, ${yearStr}`;
  const weekFolderName = weekFolderNameFromDate(date);

  // 2. Group items into rows, locate the column-header row, bucket data rows.
  const rows = groupRows(items);
  const header = findHeaderAnchors(rows);
  if (!header) {
    throw new Error(
      "Could not find Time / Patient / Provider / Appointment Type column headers in the PDF.",
    );
  }
  const dataRows = rows.slice(header.rowIndex + 1);
  // In this PDF, a single appointment spans 2–3 visual lines: the first line
  // carries the time + first chunk of the wrapping patient/provider/type, and
  // subsequent (no-time) lines carry the rest. So we walk the visual rows in
  // order, bucket each one, and merge continuation rows into the most recent
  // time-bearing row by joining each column with a space.
  const TIME_CELL_RE = /^\d{1,2}:\d{2}\s*(AM|PM)$/i;
  const parsed: AppointmentRow[] = [];
  for (const r of dataRows) {
    const text = rowToText(r);
    if (!text || text.length < 3) continue;
    if (/^page\s+\d+/i.test(text)) continue;
    if (
      text.toLowerCase().includes("time") &&
      text.toLowerCase().includes("patient") &&
      text.toLowerCase().includes("provider")
    ) {
      // Repeated header on page 2+.
      continue;
    }
    // "Total Appointments:" / "Provider(s):" preamble at the top.
    if (/^(total\s+appointments|provider\(s\))/i.test(text)) continue;

    const bucketed = bucketByColumn(r, header.target, header.all);
    const hasTime = TIME_CELL_RE.test(bucketed.time);

    if (hasTime) {
      parsed.push(bucketed);
      continue;
    }
    // No time → continuation of the previous appointment. Append the
    // non-empty cells to the prior row.
    const prev = parsed[parsed.length - 1];
    if (!prev) continue; // No appointment to attach to yet.
    const merge = (a: string, b: string) =>
      [a, b].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    prev.patient = merge(prev.patient, bucketed.patient);
    prev.provider = merge(prev.provider, bucketed.provider);
    prev.appointmentType = merge(prev.appointmentType, bucketed.appointmentType);
  }

  const csvText = rowsToCsv(parsed);
  return { sourceDateLabel, weekFolderName, rows: parsed, csvText };
}
